import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import {
  EMBED_BENCHMARK_LABELS,
  EMBED_BENCHMARK_TICKERS,
  EMBED_RANGE_KEYS,
  buildEmbedIframeSnippet,
  buildEmbedUrl,
  normalizeEmbedWidget,
} from '../../utils/embedConfig';
import './BrandEmbedWidgetBuilder.css';

function toggleInList(list, value, max) {
  const set = new Set(list);
  if (set.has(value)) set.delete(value);
  else if (!max || set.size < max) set.add(value);
  return Array.from(set);
}

function parseDomainsFromText(raw) {
  return Array.from(new Set(
    String(raw || '').split(/[\n,]+/).map((d) => d.trim()).filter(Boolean),
  ));
}

export default function BrandEmbedWidgetBuilder({
  entitySlug,
  displayName,
  embedWidget,
  onChange,
}) {
  const [publicStrategies, setPublicStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [copied, setCopied] = useState('');
  const [domainsDraft, setDomainsDraft] = useState('');

  const widget = useMemo(
    () => normalizeEmbedWidget({ ...embedWidget, entity: entitySlug }),
    [embedWidget, entitySlug],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(entitySlug)}/embed-options`,
          { headers: getBearerAuthHeaders() },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success) {
          throw new Error(data.error || `Couldn't load embed options (HTTP ${res.status})`);
        }
        setPublicStrategies(data.publicStrategies || []);
      } catch (err) {
        if (!cancelled) {
          setPublicStrategies([]);
          setLoadError(err.message || 'Failed to load public strategies.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [entitySlug]);

  const allowedDomainsKey = (widget.allowedDomains || []).join('\n');
  useEffect(() => {
    setDomainsDraft(allowedDomainsKey);
  }, [entitySlug, allowedDomainsKey]);

  const patch = useCallback((key, value) => {
    onChange({ ...widget, [key]: value });
  }, [onChange, widget]);

  const previewUrl = useMemo(
    () => buildEmbedUrl({ ...widget, entity: entitySlug }, { origin: window.location.origin }),
    [widget, entitySlug],
  );
  const iframeSnippet = useMemo(
    () => buildEmbedIframeSnippet({ ...widget, entity: entitySlug }, { origin: window.location.origin }),
    [widget, entitySlug],
  );

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    } catch (_err) {
      setCopied('');
    }
  };

  return (
    <div className="bew-builder">
      <h3 className="ae-stat-group">
        Embed widget
        <span className="ae-stat-group-note">
          Public performance chart for {displayName} client sites. Only registry strategies
          marked Public appear here. Allowed domains control where the iframe may render.
        </span>
      </h3>

      {loadError ? <p className="ae-error">{loadError}</p> : null}
      {loading ? <p className="ae-empty">Loading public strategies…</p> : null}

      {!loading && !publicStrategies.length ? (
        <p className="bew-hint">
          No public strategies on this brand yet. Mark strategies Public in Strategy Data first.
        </p>
      ) : null}

      <div className="bew-grid">
        <div className="bew-col">
          <fieldset className="bew-fieldset">
            <legend>Strategies (max 6, public only)</legend>
            <div className="bew-check-grid">
              {publicStrategies.map((s) => {
                const name = s.displayName || s.strategyName;
                const checked = widget.strategies.includes(name);
                return (
                  <label key={s.slug || name} className={`bew-check${checked ? ' is-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => patch(
                        'strategies',
                        toggleInList(widget.strategies, name, 6),
                      )}
                    />
                    <span>{name}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="bew-fieldset">
            <legend>Benchmarks</legend>
            <div className="bew-check-grid">
              {EMBED_BENCHMARK_TICKERS.map((ticker) => (
                <label key={ticker} className={`bew-check${widget.benchmarks.includes(ticker) ? ' is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={widget.benchmarks.includes(ticker)}
                    onChange={() => patch('benchmarks', toggleInList(widget.benchmarks, ticker))}
                  />
                  <span>{EMBED_BENCHMARK_LABELS[ticker]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="ae-fields bew-fields-compact">
            <label>
              <span>Default range</span>
              <select value={widget.range} onChange={(ev) => patch('range', ev.target.value)}>
                {EMBED_RANGE_KEYS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label>
              <span>Starting capital</span>
              <input
                type="number"
                min={1000}
                max={100000000}
                step={1000}
                value={widget.capital}
                onChange={(ev) => patch('capital', Number(ev.target.value))}
              />
            </label>
            <label>
              <span>Theme</span>
              <select value={widget.theme} onChange={(ev) => patch('theme', ev.target.value)}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>
            {widget.theme === 'light' ? (
              <label>
                <span>Background (hex)</span>
                <input
                  placeholder="f8fcff"
                  value={widget.bg || ''}
                  onChange={(ev) => patch('bg', ev.target.value.replace(/^#/, ''))}
                />
              </label>
            ) : null}
            <label>
              <span>Strategy colors (hex, comma-separated)</span>
              <input
                placeholder="2563eb,8b5cf6,0d9488"
                value={(widget.colors || []).join(',')}
                onChange={(ev) => patch(
                  'colors',
                  ev.target.value.split(',').map((c) => c.trim().replace(/^#/, '')).filter(Boolean),
                )}
              />
            </label>
            <label>
              <span>Title</span>
              <input
                placeholder="RiskLo performance dashboard"
                value={widget.title || ''}
                onChange={(ev) => patch('title', ev.target.value)}
              />
            </label>
            <label>
              <span>Badge</span>
              <input
                placeholder="Live trading since 2022"
                value={widget.badge || ''}
                onChange={(ev) => patch('badge', ev.target.value)}
              />
            </label>
            <label className="bew-check-inline">
              <input
                type="checkbox"
                checked={widget.controls}
                onChange={(ev) => patch('controls', ev.target.checked)}
              />
              <span>Show in-widget controls (range, benchmarks, theme)</span>
            </label>
          </div>

          <label className="bew-domains">
            <span>Allowed domains (one hostname per line)</span>
            <textarea
              rows={4}
              placeholder={'paradigmalgo.com\nwww.paradigmalgo.com'}
              value={domainsDraft}
              onChange={(ev) => {
                const raw = ev.target.value;
                setDomainsDraft(raw);
                patch('allowedDomains', parseDomainsFromText(raw));
              }}
            />
            <small className="ae-field-note">
              Hostnames only — no https://. One per line (Enter) or comma-separated.
              Saving updates the live iframe allowlist (may take a minute).
            </small>
          </label>
        </div>

        <div className="bew-col bew-preview-col">
          <div className="bew-preview-head">
            <span>Preview</span>
            <button type="button" className="ae-btn ae-btn-ghost" onClick={() => window.open(previewUrl, '_blank', 'noopener')}>
              Open full page
            </button>
          </div>
          <iframe
            className="bew-preview-frame"
            src={previewUrl}
            title={`${displayName} embed preview`}
            loading="lazy"
          />

          <div className="bew-code-block">
            <div className="bew-code-head">
              <span>Embed code</span>
              <button
                type="button"
                className="ae-btn ae-btn-primary"
                onClick={() => copyText(iframeSnippet, 'code')}
              >
                {copied === 'code' ? 'Copied' : 'Copy iframe'}
              </button>
            </div>
            <pre className="bew-code"><code>{iframeSnippet}</code></pre>
          </div>

          <div className="bew-code-block">
            <div className="bew-code-head">
              <span>Direct URL</span>
              <button
                type="button"
                className="ae-btn ae-btn-ghost"
                onClick={() => copyText(previewUrl, 'url')}
              >
                {copied === 'url' ? 'Copied' : 'Copy URL'}
              </button>
            </div>
            <pre className="bew-code bew-code-url"><code>{previewUrl}</code></pre>
          </div>
        </div>
      </div>
    </div>
  );
}
